import { useState } from 'react';
import { Link } from 'react-router-dom';
import PeriodSelectorBar from '../components/common/PeriodSelectorBar';
import { usePeriodFilter } from '../context/PeriodFilterContext';
import {
  useAnalytics360,
  useVendorAnalytics,
  useCategoryAnalytics,
} from '../api/useAnalytics';
import { extractString, extractNumber } from '../utils/sanitizeData';

export default function AnalyticsPage() {
  const { selectedRange, dateParams } = usePeriodFilter();
  const [vendorPage, setVendorPage] = useState(1);

  const apiPeriod =
    selectedRange === 'MTD'
      ? 'mtd'
      : selectedRange === 'YTD'
      ? 'ytd'
      : selectedRange === 'Recent'
      ? '30d'
      : 'custom';

  const { data: analytics360, isLoading: is360Loading } = useAnalytics360({
    period: apiPeriod,
    ...dateParams,
  });

  const { data: vendorData, isLoading: isVendorsLoading } = useVendorAnalytics({
    period: apiPeriod,
    page: vendorPage,
    page_size: 10,
    ...dateParams,
  });

  const { data: categoryData, isLoading: isCategoriesLoading } = useCategoryAnalytics({
    period: apiPeriod,
    ...dateParams,
  });

  const primaryCurrency = analytics360?.currency || 'EUR';
  const currencySymbol = primaryCurrency === 'USD' ? '$' : primaryCurrency === 'EUR' ? '€' : primaryCurrency;

  const totalSpendAmount = extractString(analytics360?.total_spend?.primary?.amount, '0.00');
  const totalSpendTrend = analytics360?.total_spend?.primary?.trend_percentage;
  const invoiceTotalCount = extractNumber(analytics360?.invoice_count?.total, 0);
  const invoiceCountTrend = analytics360?.invoice_count?.trend_percentage;

  const highestInv = analytics360?.highest_invoice;
  const lowestInv = analytics360?.lowest_invoice;
  const insights = analytics360?.insights || [];

  return (
    <div className="max-w-[1360px] mx-auto py-2 sm:py-4">
      {/* Header & Period Filters */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <div className="text-[11.5px] font-mono font-semibold tracking-wider text-brand-vib uppercase dark:text-brand-light">
            / FINANCIAL INTELLIGENCE
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-brand-navy dark:text-white tracking-tight mt-0.5">
            Analytics 360
          </h1>
          <p className="text-xs sm:text-sm text-brand-muted dark:text-bodydark mt-1">
            Supplier spend distribution, multi-currency cashflows, and AI anomaly detection.
          </p>
        </div>

        {/* Period Selector Bar & Base Currency Badge */}
        <PeriodSelectorBar
          showExtraActions={
            <span className="px-2.5 py-1.5 rounded-xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark shadow-card text-[11px] font-mono font-bold text-brand-vib dark:text-brand-300">
              Base: {primaryCurrency}
            </span>
          }
        />
      </div>

      {/* Metrics Row: Spend, Count, Highest, Lowest */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Total Spend */}
        <div className="rounded-2.5xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark p-5 shadow-card">
          <div className="text-[10px] font-mono font-bold text-brand-soft uppercase tracking-wider mb-1">
            Total Spend ({primaryCurrency})
          </div>
          <div className="text-2xl font-mono font-bold text-brand-navy dark:text-white">
            {is360Loading ? '—' : `${currencySymbol}${parseFloat(totalSpendAmount || '0').toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          </div>
          <div className="text-xs font-mono mt-1 flex items-center gap-1.5">
            {totalSpendTrend !== null && totalSpendTrend !== undefined ? (
              <span className={totalSpendTrend >= 0 ? 'text-rose-600 font-bold' : 'text-brand-green font-bold'}>
                {totalSpendTrend >= 0 ? `+${totalSpendTrend}%` : `${totalSpendTrend}%`} vs prev
              </span>
            ) : (
              <span className="text-brand-soft">No previous trend</span>
            )}
          </div>
        </div>

        {/* Invoice Volume */}
        <div className="rounded-2.5xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark p-5 shadow-card">
          <div className="text-[10px] font-mono font-bold text-brand-soft uppercase tracking-wider mb-1">
            Processed Volume
          </div>
          <div className="text-2xl font-mono font-bold text-brand-navy dark:text-white">
            {is360Loading ? '—' : `${invoiceTotalCount} invoices`}
          </div>
          <div className="text-xs font-mono mt-1 flex items-center gap-1.5">
            {invoiceCountTrend !== null && invoiceCountTrend !== undefined ? (
              <span className="text-brand-vib font-bold">
                {invoiceCountTrend >= 0 ? `+${invoiceCountTrend}%` : `${invoiceCountTrend}%`}
              </span>
            ) : (
              <span className="text-brand-soft">All sources</span>
            )}
          </div>
        </div>

        {/* Highest Invoice Extreme */}
        <div className="rounded-2.5xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark p-5 shadow-card">
          <div className="text-[10px] font-mono font-bold text-brand-soft uppercase tracking-wider mb-1">
            Highest Single Invoice
          </div>
          {highestInv ? (
            <div>
              <div className="text-xl font-mono font-bold text-brand-navy dark:text-white">
                {currencySymbol}{parseFloat(highestInv.amount || '0').toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <Link
                to={`/invoices/${highestInv.id}`}
                className="text-xs font-semibold text-brand-vib hover:underline truncate block mt-0.5"
              >
                {highestInv.vendor_name} ({highestInv.invoice_date}) →
              </Link>
            </div>
          ) : (
            <div className="text-xs font-mono text-brand-soft mt-1">No invoices in period</div>
          )}
        </div>

        {/* Lowest Invoice Extreme */}
        <div className="rounded-2.5xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark p-5 shadow-card">
          <div className="text-[10px] font-mono font-bold text-brand-soft uppercase tracking-wider mb-1">
            Lowest Single Invoice
          </div>
          {lowestInv ? (
            <div>
              <div className="text-xl font-mono font-bold text-brand-navy dark:text-white">
                {currencySymbol}{parseFloat(lowestInv.amount || '0').toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <Link
                to={`/invoices/${lowestInv.id}`}
                className="text-xs font-semibold text-brand-vib hover:underline truncate block mt-0.5"
              >
                {lowestInv.vendor_name} ({lowestInv.invoice_date}) →
              </Link>
            </div>
          ) : (
            <div className="text-xs font-mono text-brand-soft mt-1">No invoices in period</div>
          )}
        </div>
      </div>

      {/* AI Spending Insights & Anomalies Grid */}
      {insights.length > 0 && (
        <div className="rounded-2.5xl bg-white dark:bg-boxdark border border-brand-green/35 dark:border-brand-green/20 p-5 sm:p-6 shadow-card mb-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-[#12b98a] to-[#16EA9E] flex items-center justify-center text-brand-navy font-bold text-xs shadow-xs">
              AI
            </div>
            <h2 className="text-xs font-mono font-bold uppercase tracking-wider text-[#0e9c6c] dark:text-brand-green">
              AI Spending Anomalies & Financial Insights ({insights.length})
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {insights.map((ins, idx) => (
              <div
                key={idx}
                className={`p-3.5 rounded-xl border flex flex-col gap-1.5 ${
                  ins.type === 'spend_alert'
                    ? 'bg-amber-50/70 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-200'
                    : ins.type === 'vendor_risk'
                    ? 'bg-rose-50/70 dark:bg-rose-950/30 border-rose-200 dark:border-rose-800 text-rose-900 dark:text-rose-200'
                    : 'bg-emerald-50/70 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800 text-emerald-900 dark:text-emerald-200'
                }`}
              >
                <div className="text-xs font-bold font-mono uppercase tracking-wide">
                  {ins.title}
                </div>
                <div className="text-xs leading-relaxed opacity-90">
                  {ins.message}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Grid: Full Supplier Ranking & Tag Categories */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Full Supplier Spend Distribution (2 Cols) */}
        <div className="lg:col-span-2 rounded-2.5xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark p-5 sm:p-6 shadow-card">
          <div className="flex items-center justify-between pb-4 border-b border-brand-line dark:border-strokedark mb-4">
            <div>
              <h2 className="text-base font-bold text-brand-navy dark:text-white tracking-tight">
                Supplier Spend Ranking
              </h2>
              <p className="text-xs text-brand-muted dark:text-bodydark mt-0.5">
                Total spend volume per vendor in base currency ({primaryCurrency}).
              </p>
            </div>
            <span className="text-xs font-mono text-brand-soft">
              {vendorData?.results?.length || 0} suppliers
            </span>
          </div>

          {isVendorsLoading ? (
            <div className="py-8 text-center text-xs font-mono text-brand-muted">Loading vendor ranking…</div>
          ) : !vendorData?.results || vendorData.results.length === 0 ? (
            <div className="py-8 text-center text-xs font-mono text-brand-muted">No vendor expenses found for this period.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-brand-blue-tint dark:bg-brand-900/30 border-b border-brand-line dark:border-strokedark text-[11px] font-mono font-bold uppercase text-brand-vib dark:text-brand-300">
                    <th className="py-2.5 px-3">Supplier</th>
                    <th className="py-2.5 px-3 text-right">Invoices</th>
                    <th className="py-2.5 px-3 text-right">Avg Ticket</th>
                    <th className="py-2.5 px-3 text-right">Total ({primaryCurrency})</th>
                    <th className="py-2.5 px-3 text-right w-28">Share</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-brand-line dark:divide-strokedark text-xs font-mono">
                  {vendorData.results.map((v, i) => (
                    <tr key={i} className="hover:bg-brand-bg/40 transition-colors">
                      <td className="py-3 px-3 font-semibold text-brand-navy dark:text-white">
                        {v.vendor_name}
                      </td>
                      <td className="py-3 px-3 text-right text-brand-muted">
                        {v.invoices_count}
                      </td>
                      <td className="py-3 px-3 text-right text-brand-muted">
                        {currencySymbol}{parseFloat(v.average_amount || '0').toFixed(2)}
                      </td>
                      <td className="py-3 px-3 text-right font-bold text-brand-navy dark:text-white">
                        {currencySymbol}{parseFloat(v.total_amount || '0').toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="py-3 px-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-12 h-2 rounded-full bg-brand-line dark:bg-strokedark overflow-hidden">
                            <div
                              className="h-full bg-brand-vib rounded-full"
                              style={{ width: `${Math.min(100, v.percentage || 0)}%` }}
                            />
                          </div>
                          <span className="font-semibold text-brand-navy dark:text-white">
                            {v.percentage ? `${v.percentage.toFixed(1)}%` : '0%'}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination Controls */}
          {vendorData?.pagination && vendorData.pagination.total > 10 && (
            <div className="flex items-center justify-between pt-4 border-t border-brand-line dark:border-strokedark mt-4 text-xs font-mono">
              <span className="text-brand-muted">
                Page {vendorData.pagination.page} of {Math.ceil(vendorData.pagination.total / vendorData.pagination.page_size)}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={vendorPage <= 1}
                  onClick={() => setVendorPage((p) => Math.max(1, p - 1))}
                  className="px-3 py-1.5 rounded-lg border border-brand-line dark:border-strokedark disabled:opacity-40 hover:bg-brand-bg dark:hover:bg-strokedark cursor-pointer"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={!vendorData.pagination.has_next}
                  onClick={() => setVendorPage((p) => p + 1)}
                  className="px-3 py-1.5 rounded-lg border border-brand-line dark:border-strokedark disabled:opacity-40 hover:bg-brand-bg dark:hover:bg-strokedark cursor-pointer"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Tag-Based Category Spend Distribution (1 Col) */}
        <div className="rounded-2.5xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark p-5 sm:p-6 shadow-card flex flex-col justify-between">
          <div>
            <div className="pb-4 border-b border-brand-line dark:border-strokedark mb-4">
              <h2 className="text-base font-bold text-brand-navy dark:text-white tracking-tight">
                Spend by Tag Category
              </h2>
              <p className="text-xs text-brand-muted dark:text-bodydark mt-0.5">
                Horizontal tag distribution (multi-tag overlap supported).
              </p>
            </div>

            {isCategoriesLoading ? (
              <div className="py-8 text-center text-xs font-mono text-brand-muted">Loading categories…</div>
            ) : !categoryData || categoryData.length === 0 ? (
              <div className="py-8 text-center text-xs font-mono text-brand-muted">No tagged categories found.</div>
            ) : (
              <div className="space-y-4">
                {categoryData.map((cat, i) => (
                  <div key={i} className="flex flex-col gap-1 text-xs font-mono">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-brand-navy dark:text-white truncate">
                        {cat.tag ? `#${cat.tag}` : cat.label || 'Untagged'}
                      </span>
                      <span className="font-bold text-brand-vib dark:text-brand-300">
                        {currencySymbol}{parseFloat(cat.total_amount || '0').toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="w-full h-2 rounded-full bg-brand-bg dark:bg-strokedark overflow-hidden">
                      <div
                        className="h-full bg-brand-vib rounded-full"
                        style={{ width: `${Math.min(100, cat.percentage || 0)}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-[10px] text-brand-soft">
                      <span>{cat.invoices_count} invoices</span>
                      <span>{cat.percentage?.toFixed(1)}% of volume</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="p-3 mt-4 rounded-xl bg-brand-bg/60 dark:bg-strokedark/30 border border-brand-line dark:border-strokedark text-[11px] text-brand-soft leading-relaxed">
            💡 Tags can overlap across multi-tag invoices, allowing comprehensive multi-dimensional classification.
          </div>
        </div>
      </div>
    </div>
  );
}
